import { useEffect } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/stores/auth';
import { useUi } from '@/stores/ui';
import type { Branch, Category, ClientSummary, Service, SalonSettings, Staff, Tag } from '@/types';

export const qk = {
  me: ['me'] as const,
  settings: ['settings'] as const,
  branches: ['branches'] as const,
  categories: ['categories'] as const,
  services: (includeInactive = false) => ['services', { includeInactive }] as const,
  staff: (branchId?: string | null, includeInactive = false) => ['staff', { branchId, includeInactive }] as const,
  tags: ['tags'] as const,
  clients: (params: object) => ['clients', params] as const,
  client: (id: string) => ['client', id] as const,
  calendar: (params: object) => ['calendar', params] as const,
  appointment: (id: string) => ['appointment', id] as const,
  invoices: (params: object) => ['invoices', params] as const,
  invoice: (id: string) => ['invoice', id] as const,
  walkins: (branchId: string) => ['walkins', branchId] as const,
  dashboard: (branchId?: string | null) => ['dashboard', branchId] as const,
};

export function useBranches() {
  return useQuery({ queryKey: qk.branches, queryFn: () => api.get<Branch[]>('/salon/branches'), staleTime: 5 * 60_000 });
}

/** The branch selected in the top bar, falling back to the first accessible branch. */
export function useActiveBranch() {
  const { data: branches } = useBranches();
  const branchId = useUi((s) => s.branchId);
  const setBranch = useUi((s) => s.setBranch);
  const active = branches?.find((b) => b.id === branchId) ?? branches?.[0] ?? null;
  useEffect(() => {
    if (active && active.id !== branchId) setBranch(active.id);
  }, [active, branchId, setBranch]);
  return { branch: active, branches: branches ?? [], setBranch };
}

export function useSettings() {
  return useQuery({ queryKey: qk.settings, queryFn: () => api.get<SalonSettings>('/salon/settings'), staleTime: 5 * 60_000 });
}

export function useCategories() {
  return useQuery({ queryKey: qk.categories, queryFn: () => api.get<Category[]>('/catalog/categories'), staleTime: 60_000 });
}

export function useServices(includeInactive = false) {
  return useQuery({
    queryKey: qk.services(includeInactive),
    queryFn: async () => (await api.page<Service>('/catalog/services', { pageSize: 500, includeInactive })).items,
    staleTime: 60_000,
  });
}

export function useStaff(branchId?: string | null, includeInactive = false) {
  const enabled = useAuth((s) => !!s.session?.salon);
  return useQuery({
    queryKey: qk.staff(branchId, includeInactive),
    queryFn: () => api.get<Staff[]>('/staff', { branchId: branchId ?? undefined, includeInactive }),
    staleTime: 60_000,
    enabled,
  });
}

export function useTags() {
  return useQuery({ queryKey: qk.tags, queryFn: () => api.get<Tag[]>('/clients/tags'), staleTime: 60_000 });
}

export function useClientSearch(q: string, enabled = true) {
  return useQuery({
    queryKey: qk.clients({ q, picker: true }),
    queryFn: async () => (await api.page<ClientSummary>('/clients', { q, pageSize: 8 })).items,
    enabled: enabled && q.trim().length >= 2,
    placeholderData: keepPreviousData,
  });
}

export function useInvalidate() {
  const qc = useQueryClient();
  return (...keys: readonly unknown[][]) => Promise.all(keys.map((queryKey) => qc.invalidateQueries({ queryKey })));
}
